// Loaded messages carry `id`, streamed ones only `message_id`, and a locally added
// prompt only `requestId`; all three derive from the same executor-assigned id.
const messageKey = (message) => message?.id ?? message?.message_id ?? message?.requestId ?? null;

/**
 * Ids of the messages a retry replaces: the resubmitted user message and everything
 * after it. Retrying the same turn again adds the next attempt's ids, so an
 * accumulated set describes every attempt the forked run leaves behind.
 */
export const collectSupersededAttemptIds = (messages = [], promptMessage) => {
  const key = messageKey(promptMessage);
  if (!key) return [];

  const attemptStart = messages.findIndex((msg) => messageKey(msg) === key);
  if (attemptStart === -1) return [];

  return messages.slice(attemptStart).map(messageKey).filter(Boolean);
};

/**
 * The conversation the resumed run actually replays: the flat message log minus
 * the attempts a retry superseded.
 */
export const excludeSupersededMessages = (messages = [], supersededIds) => {
  if (!supersededIds?.size) return messages;

  return messages.filter((msg) => !supersededIds.has(messageKey(msg)));
};

export const getMessagesToProcess = (messages, lastProcessedMessageId) => {
  if (!messages || messages.length === 0) {
    return {
      toProcess: [],
      lastProcessedMessageId: null,
    };
  }

  // First run or log shrank (truncate / reset): process everything.
  const isFirstRunOrReset = lastProcessedMessageId === null;
  let startIndex;
  if (isFirstRunOrReset) {
    startIndex = 0;
  } else {
    startIndex = messages.findIndex((msg) => msg.message_id === lastProcessedMessageId);
    if (startIndex === -1) {
      startIndex = 0;
    }
  }

  const toProcess = messages.slice(startIndex);

  return {
    toProcess,
    lastProcessedMessageId: messages.at(-1).message_id,
  };
};
